import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { COUNTRIES } from '../data/countries';
import TermsModal from '../components/TermsModal';

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    country: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [hasViewedTerms, setHasViewedTerms] = useState(false);
  
  const { register } = useAuth();
  const navigate = useNavigate();
  useDocumentTitle('Cognidaily | Register');

  useEffect(() => {
    if (formData.country) {
      return;
    }

    // Detect country from IP geolocation
    const detectCountry = async () => {
      try {
        const response = await fetch('/api/auth/country', {
          method: 'GET',
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          const countryCode = data.data?.countryCode;
          
          if (countryCode && COUNTRIES.some(country => country.code === countryCode)) {
            setFormData(prev => ({ ...prev, country: countryCode }));
            return;
          }
        }
      } catch {
        // Country detection failed - user can select manually
      }

      // Fallback to browser locale if IP geolocation fails
      const locale = navigator.language || (Array.isArray(navigator.languages) ? navigator.languages[0] : '');
      if (!locale) return;

      const parts = locale.split('-');
      if (parts.length < 2) return;

      const guessedCode = parts[1].toUpperCase();
      if (COUNTRIES.some(country => country.code === guessedCode)) {
        setFormData(prev => ({ ...prev, country: guessedCode }));
      }
    };

    detectCountry();
  }, [formData.country]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const validateForm = () => {
    const usernameRegex = /^[a-zA-Z0-9._-]+$/;
    if (!usernameRegex.test(formData.username)) {
      setError('Username can only include letters, numbers, underscores, hyphens, or periods');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    
    if (formData.password.length > 128) {
      setError('Password must be no more than 128 characters long');
      return false;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9\s])(?!.*\s).{8,128}$/;
    if (!passwordRegex.test(formData.password)) {
      setError('Password must be 8-128 characters and include uppercase, lowercase, number, and symbol (no spaces)');
      return false;
    }

    if (!formData.country) {
      setError('Please select your country so we can place you on the right leaderboard');
      return false;
    }

    return true;
  };

  // Apple Sign In handler removed - OAuth buttons are disabled
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _handleAppleSignIn = async () => {
    try {
      setError('');
      setLoading(true);
      
      // Check if Apple Sign In is available
      if (typeof window !== 'undefined' && (window as any).AppleID) {
        const AppleID = (window as any).AppleID;
        AppleID.auth.init({
          clientId: import.meta.env.VITE_APPLE_CLIENT_ID || '',
          scope: 'name email',
          redirectURI: window.location.origin,
          usePopup: true
        });

        const response = await AppleID.auth.signIn();
        
        if (response.authorization) {
          const { id_token, code, user } = response.authorization;
          
          const apiResponse = await fetch('/api/auth/apple', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              identityToken: id_token,
              authorizationCode: code,
              user: user
            })
          });

          const data = await apiResponse.json();
          
          if (data.success) {
            navigate('/');
          } else {
            setError(data.error || 'Apple Sign In failed');
          }
        }
      } else {
        setError('Apple Sign In is not available. Please use a supported browser.');
      }
    } catch (err: any) {
      setError(err.message || 'Apple Sign In failed');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await axios.post('/api/auth/register/check', {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        country: formData.country
      });

      setShowTermsModal(true);
      setHasViewedTerms(true);
    } catch (err: any) {
      const status = err?.response?.status;
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Unable to verify registration details. Please try again.';

      if (status === 404 || (typeof message === 'string' && message.includes('Route not found'))) {
        setShowTermsModal(true);
        setHasViewedTerms(true);
        return;
      }

      setError(String(message || 'Connection failed. Is the server running?'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    // Double-check that terms were viewed and accepted
    if (!hasViewedTerms) {
      console.error('[Register] Attempted to create account without viewing terms');
      setError('Please review the terms and policies first.');
      setShowTermsModal(true);
      return;
    }

    if (!termsAccepted) {
      setError('Please confirm that you understand the terms and policies.');
      return;
    }

    setShowTermsModal(false);
    setLoading(true);

    try {
      await register(formData.username, formData.email, formData.password, formData.country);
      navigate('/');
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <section className="section section--auth">
        <h1>Create an account</h1>
        <p>Pick a handle, choose your country, and join the daily set.</p>
        {error && (
          <div role="alert" className="alert alert-error">
            {error}
          </div>
        )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleNext(e);
        }}
        className="auth-form"
        noValidate
      >
        <div className="form-group">
            <label htmlFor="username" className="form-label">
              Username
            </label>
            <input
              type="text"
              id="username"
              name="username"
              className="form-input"
              value={formData.username}
              onChange={handleChange}
              placeholder="Choose a username"
              required
              minLength={3}
              maxLength={30}
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className="form-input"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="country" className="form-label">
              Country
            </label>
            <select
              id="country"
              name="country"
              className="form-input"
              value={formData.country}
              onChange={handleChange}
              disabled={loading}
              required
            >
              <option value="">Select your country</option>
              {COUNTRIES.map(country => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
            <small className="form-helper">
              Used for regional leaderboards, badges, and personalized features.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                className="form-input"
                value={formData.password}
                onChange={handleChange}
                placeholder="Create a strong password"
                required
                minLength={8}
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                disabled={loading}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            </div>
            <small className="form-helper">
              At least 8 characters with uppercase, lowercase, number, and symbol.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword" className="form-label">
              Confirm Password
            </label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                className="form-input"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                required
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                disabled={loading}
              >
                {showConfirmPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

        <button
          type="submit"
          className="btn btn-primary btn-full"
          disabled={loading}
        >
          {loading ? 'Checking...' : 'Next'}
        </button>
      </form>

        <TermsModal
          isOpen={showTermsModal}
          onClose={() => {
            setShowTermsModal(false);
            // Don't reset termsAccepted or hasViewedTerms when closing
            // User can close and reopen, but once they've viewed, they've viewed
          }}
          onAccept={handleCreateAccount}
          accepted={termsAccepted}
          onAcceptChange={setTermsAccepted}
        />

        {/* OAuth login buttons disabled */}
        {/* <div className="oauth-divider">
          <span>or</span>
        </div>

        <div className="oauth-buttons">
          <a
            href="/api/auth/google"
            className={`btn btn-oauth btn-google ${loading ? 'disabled' : ''}`}
            onClick={(e) => loading && e.preventDefault()}
            style={{ pointerEvents: loading ? 'none' : 'auto', opacity: loading ? 0.6 : 1 }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.64 9.20454C17.64 8.56636 17.5827 7.95272 17.4764 7.36363H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8195H14.9564C16.6582 14.2527 17.64 11.9455 17.64 9.20454Z" fill="#4285F4"/>
              <path d="M9 18C11.43 18 13.467 17.1941 14.9564 15.8195L12.0477 13.5614C11.2418 14.1014 10.2109 14.4204 9 14.4204C6.65455 14.4204 4.67182 12.8373 3.96409 10.71H0.957275V13.0418C2.43818 15.9832 5.48182 18 9 18Z" fill="#34A853"/>
              <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957273C0.347727 6.17318 0 7.54773 0 9C0 10.4523 0.347727 11.8268 0.957273 13.0418L3.96409 10.71Z" fill="#FBBC05"/>
              <path d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65455 3.57955 9 3.57955Z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </a>
          <button
            type="button"
            className="btn btn-oauth btn-apple"
            onClick={() => handleAppleSignIn()}
            disabled={loading}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.5625 0.5625C13.3125 0.75 12.1875 1.5 12.1875 3C12.1875 4.5 13.125 5.25 13.3125 5.4375C13.125 5.625 12.1875 6.5625 12.1875 8.0625C12.1875 9.5625 13.125 10.3125 13.3125 10.5C13.125 10.6875 12.1875 11.4375 12.1875 12.9375C12.1875 14.4375 13.3125 15.1875 13.5625 15.375C13.75 15.5625 14.4375 15.5625 14.625 15.5625C14.8125 15.5625 15.5 15.5625 15.6875 15.375C15.9375 15.1875 17.0625 14.4375 17.0625 12.9375C17.0625 11.4375 16.125 10.6875 15.9375 10.5C16.125 10.3125 17.0625 9.5625 17.0625 8.0625C17.0625 6.5625 16.125 5.8125 15.9375 5.625C16.125 5.4375 17.0625 4.5 17.0625 3C17.0625 1.5 15.9375 0.75 15.6875 0.5625C15.5 0.375 14.8125 0.375 14.625 0.375C14.4375 0.375 13.75 0.375 13.5625 0.5625Z" fill="currentColor"/>
            </svg>
            Continue with Apple
          </button>
        </div> */}

        <p>Already registered? <Link to="/login" className="link">Sign in</Link></p>
    </section>
  );
};

export default Register;
